import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';

export enum CancellationType {
  BOOKING = 'booking',
  WAITLIST = 'waitlist',
}

const cancellationConfig: Record<CancellationType, { translationKey: string; ctaColor: 'error' | 'warning' }> = {
  [CancellationType.BOOKING]: {
    translationKey: 'manage_passes.cancelBooking',
    ctaColor: 'error',
  },
  [CancellationType.WAITLIST]: {
    translationKey: 'manage_passes.cancelWaitlist',
    ctaColor: 'warning',
  },
};

type CancellationSheetProps = {
  isOpen: boolean;
  onClose: (confirmed: boolean) => void | Promise<void>;
  experienceName: string;
  bookingDate: string;
  cancellationType?: CancellationType;
  cancelHoursBefore?: number;
  cancelAllowed?: boolean;
  guestName?: string;
};

export const CancellationSheet = ({
  isOpen,
  onClose,
  experienceName,
  bookingDate,
  cancellationType = CancellationType.BOOKING,
  cancelHoursBefore,
  cancelAllowed = true,
  guestName,
}: CancellationSheetProps) => {
  const { t } = useTranslation();
  const { translationKey, ctaColor } = cancellationConfig[cancellationType];
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await onClose(true);
    } catch (err) {
      setError(t('manage_passes.cancelBooking.error'));
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const isBookingCancel = cancellationType === CancellationType.BOOKING;
  const showPolicy = isBookingCancel && cancelAllowed && cancelHoursBefore !== undefined && cancelHoursBefore > 0;
  const showClosedWarning = isBookingCancel && !cancelAllowed;

  return (
    <BottomSheet
      open={isOpen}
      onClose={() => onClose(false)}
      ctaText={t(`${translationKey}.confirm`)}
      ctaColor={ctaColor}
      ctaDisabled={showClosedWarning}
      isLoading={isLoading}
      onConfirm={handleConfirm}>
      <Typography variant="h6" fontWeight={700}>
        {t(`${translationKey}.title`)}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t(`${translationKey}.content`, {
          experience: experienceName,
          date: bookingDate,
        })}
      </Typography>
      {guestName && isBookingCancel && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t('manage_passes.cancelBooking.cascadeGuest', { name: guestName })}
        </Alert>
      )}
      {showPolicy && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('manage_passes.cancelBooking.policy', { hours: cancelHoursBefore })}
        </Alert>
      )}
      {showClosedWarning && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t('manage_passes.booking.cancelClosed')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}
    </BottomSheet>
  );
};
